import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import Manufacturer from '../../../../types/manufacturer/Manufacturer';
import ManufacturerWriteDTO from '../../../../types/manufacturer/ManufacturerWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsMaintainerDirective } from '../../directives/is-maintainer.directive';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { ExportService } from '../../services/export.service';
import { ManufacturerService } from '../../services/manufacturer.service';
import {
    CreateManufacturerDialogComponent
} from '../shared/manufacturers/create-manufacturer-dialog/create-manufacturer-dialog.component';
import {
    UpdateManufacturerDialogComponent
} from '../shared/manufacturers/update-manufacturer-dialog/update-manufacturer-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-manufacturers',
    templateUrl: './manufacturers.component.html',
    styleUrls: [ './manufacturers.component.scss' ],
    imports: [
        PageHeaderComponent,
        IftaLabelModule,
        IconField,
        InputIcon,
        InputText,
        FormsModule,
        TableModule,
        Button,
        CreateManufacturerDialogComponent,
        UpdateManufacturerDialogComponent,
        RouterLink,
        FormatDatePipe,
        Tooltip,
        IsMaintainerDirective
    ]
})
export class ManufacturersComponent implements OnInit {
    public manufacturers: Manufacturer[] = [];
    public totalRecords: number = 0;
    public rows: number = 10;
    public currentPage: number = 0;

    public search: string = '';
    public loading: boolean = false;

    public displayCreateManufacturerDialog: boolean = false;
    public displayUpdateManufacturerDialog: boolean = false;

    public selectedManufacturer: ManufacturerWriteDTO = new ManufacturerWriteDTO({});

    constructor(
        private manufacturerService: ManufacturerService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private exportService: ExportService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadManufacturers();
    }

    /**
     * Handles the search input change.
     */
    public onSearchInputChange(): void {
        this.currentPage = 0;
        this.loadManufacturers();
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = event.first / event.rows;
        this.rows = event.rows;
        this.loadManufacturers();
    }

    /**
     * Loads the manufacturers from the service.
     */
    public loadManufacturers(): void {
        this.loading = true;
        this.manufacturerService
            .getManufacturers(this.search, this.currentPage, this.rows)
            .then((data: Page<Manufacturer>) => {
                this.manufacturers = data.content;
                this.totalRecords = data.page.totalElements;
            })
            .catch(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load manufacturers',
                    life: 3000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Opens the create manufacturer dialog.
     */
    public openCreateManufacturerDialog(): void {
        this.displayCreateManufacturerDialog = true;
    }

    /**
     * Opens the update manufacturer dialog.
     */
    public editManufacturer(manufacturer: Manufacturer): void {
        this.selectedManufacturer = new ManufacturerWriteDTO(manufacturer);
        this.displayUpdateManufacturerDialog = true;
    }

    /**
     * Deletes the manufacturer. Confirms before deletion.
     *
     * @param manufacturer The manufacturer to delete.
     */
    public deleteManufacturer(manufacturer: Manufacturer): void {
        this.confirmationService.confirm({
            header: 'Delete Manufacturer',
            message: 'Are you sure you want to delete this manufacturer?',
            accept: () => {
                this.manufacturerService
                    .deleteManufacturer(manufacturer.id)
                    .then(() => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Manufacturer deleted successfully',
                            life: 3000,
                        });
                        this.loadManufacturers();
                    })
                    .catch((error: any) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message,
                            life: 3000,
                        });
                    });
            },
        });
    }

    /**
     * Exports the manufacturers to a file.
     */
    public exportManufacturers() {
        this.exportService.exportManufacturers().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Manufacturers exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }
}
